package dev.danko.roblox_craft_ai.components

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetRevision
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.CodeViewer
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.components.ToastManager
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div

@Composable
fun FullscreenCodeDiffModal(
    visible: Boolean,
    asset: AssetResponse?,
    onCloseRequest: () -> Unit,
    onRestoreRevision: ((AssetRevision) -> Unit)? = null
) {
    if (!visible || asset == null) return

    val palette = ColorMode.current.toAppPalette()
    val revisions = asset.revisions
    var selectedRevisionNum by remember(asset.id) { mutableStateOf<Int?>(null) }

    val currentCode = asset.luauCode ?: ""
    val selectedRev = revisions.firstOrNull { it.revisionNumber == selectedRevisionNum }
    val displayCode = selectedRev?.luauCode ?: currentCode
    val previousCode = selectedRev?.metadata?.get("previousCode") ?: asset.metadata["previousCode"]

    val displayPrompt = if (asset.prompt.isBlank()) {
        Txt.syncModal.untitledAsset
    } else if (asset.prompt.length > 80) {
        asset.prompt.take(80) + "…"
    } else {
        asset.prompt
    }

    ModalDialog(
        visible = visible,
        onCloseRequest = onCloseRequest,
        title = Txt.revisions.title,
        dismissOnBackdropClick = true,
        dismissOnEsc = true,
        modifier = Modifier
            .minWidth(60.vw)
            .maxWidth(96.vw)
            .maxHeight(92.vh)
            .minHeight(70.vh),
        footer = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (selectedRev != null && onRestoreRevision != null) {
                    AppButton(
                        text = Txt.revisions.restore(selectedRev.revisionNumber),
                        onClick = {
                            onRestoreRevision(selectedRev)
                            ToastManager.success(Txt.revisions.restored(selectedRev.revisionNumber))
                        },
                        variant = AppButtonVariant.PRIMARY,
                        size = AppButtonSize.MD
                    )
                } else {
                    Box {}
                }

                AppButton(
                    text = Txt.common.close,
                    onClick = onCloseRequest,
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.MD
                )
            }
        }
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(0.75.cssRem)) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(0.75.cssRem),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                    SpanText(
                        displayPrompt,
                        Modifier
                            .fontSize(0.9375.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .color(palette.textPrimary)
                    )

                    Div(
                        Modifier
                            .fontSize(0.75.cssRem)
                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                            .color(palette.textSecondary)
                            .toAttrs()
                    ) {
                        SpanText("#${asset.id.take(8)}")
                    }
                }

                if (revisions.isNotEmpty()) {
                    Row(modifier = Modifier.gap(0.35.cssRem), verticalAlignment = Alignment.CenterVertically) {
                        SpanText(Txt.revisions.listLabel, Modifier.fontSize(0.8.cssRem).color(palette.textSecondary))

                        val isLatest = selectedRevisionNum == null
                        Box(
                            modifier = Modifier
                                .padding(leftRight = 0.5.cssRem, topBottom = 0.2.cssRem)
                                .borderRadius(4.px)
                                .border(1.px, LineStyle.Solid, if (isLatest) palette.accent else palette.border)
                                .backgroundColor(if (isLatest) palette.accent else palette.surfaceElevated)
                                .cursor(Cursor.Pointer)
                                .onClick { selectedRevisionNum = null }
                        ) {
                            SpanText(
                                Txt.revisions.latest(revisions.size + 1),
                                Modifier
                                    .fontSize(0.75.cssRem)
                                    .fontWeight(if (isLatest) FontWeight.Bold else FontWeight.Normal)
                                    .color(if (isLatest) Color.rgb(0xFFFFFF) else palette.textPrimary)
                            )
                        }

                        revisions.forEach { rev ->
                            val isSel = selectedRevisionNum == rev.revisionNumber
                            Box(
                                modifier = Modifier
                                    .padding(leftRight = 0.5.cssRem, topBottom = 0.2.cssRem)
                                    .borderRadius(4.px)
                                    .border(1.px, LineStyle.Solid, if (isSel) palette.accent else palette.border)
                                    .backgroundColor(if (isSel) palette.accent else palette.surfaceElevated)
                                    .cursor(Cursor.Pointer)
                                    .onClick { selectedRevisionNum = rev.revisionNumber }
                            ) {
                                SpanText(
                                    "v${rev.revisionNumber}",
                                    Modifier
                                        .fontSize(0.75.cssRem)
                                        .fontWeight(if (isSel) FontWeight.Bold else FontWeight.Normal)
                                        .color(if (isSel) Color.rgb(0xFFFFFF) else palette.textPrimary)
                                )
                            }
                        }
                    }
                }
            }

            Box(Modifier.fillMaxWidth()) {
                CodeViewer(
                    code = displayCode,
                    language = "lua",
                    showLineNumbers = true,
                    showCopyButton = true,
                    maxHeight = 65.vh,
                    modifier = Modifier.fillMaxWidth(),
                    previousCode = previousCode
                )
            }
        }
    }
}
